import React, { useState, useEffect } from 'react';
import { useConnectedPlatforms } from '../hooks/usePlatformAnalytics';
import { platforms } from '../lib/platforms';
import { fetchMetaAdsData } from '../lib/platforms/meta';
import { supabase } from '../lib/supabase';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Filter,
  RefreshCw,
  AlertCircle,
  Calendar,
} from 'lucide-react';

interface AdAccount {
  account_id: string;
  name: string;
  business_name?: string;
  status: number;
}

interface MetricsState {
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  reach: number;
}

interface DateRange {
  start: Date;
  end: Date;
}

const Analytics = () => {
  const { connections, loading: connectionsLoading, error: connectionsError } = useConnectedPlatforms();
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [timeRange, setTimeRange] = useState('today');
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    start: new Date(),
    end: new Date(),
  });
  const [metrics, setMetrics] = useState<MetricsState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const getDateRange = (): DateRange => {
    const end = new Date();
    const start = new Date();

    switch (timeRange) {
      case 'today':
        // Start and end are already today
        break;
      case 'yesterday':
        start.setDate(end.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'last7days':
        start.setDate(end.getDate() - 7);
        break;
      case 'last30days':
        start.setDate(end.getDate() - 30);
        break;
      case 'thisMonth':
        start.setDate(1); // First day of current month
        break;
      case 'lastMonth':
        start.setDate(1);
        start.setMonth(start.getMonth() - 1);
        end.setDate(0); // Last day of previous month
        break;
      case 'custom':
        return customDateRange;
      default:
        // Default to today
        break;
    }

    // Set times to start and end of day
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  // First, fetch accounts when platform is selected
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!selectedPlatform) return;

      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          throw new Error('User not authenticated');
        }

        if (selectedPlatform === 'meta-ads') {
          const data = await fetchMetaAdsData();
          if (data?.accounts) {
            setAccounts(data.accounts);
            if (!selectedAccount && data.accounts.length > 0) {
              setSelectedAccount(data.accounts[0].account_id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setError('Failed to load ad accounts');
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [selectedPlatform]);

  // Then fetch metrics when account is selected or time range changes
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!selectedPlatform || !selectedAccount) return;

      setLoading(true);
      setError(null);

      try {
        const dateRange = getDateRange();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          throw new Error('User not authenticated');
        }

        // Get metrics from database for the selected date range
        const { data: metricsData, error: metricsError } = await supabase
          .from('platform_metrics')
          .select('*')
          .eq('platform_id', selectedPlatform)
          .eq('user_id', session.user.id)
          .gte('date', dateRange.start.toISOString().split('T')[0])
          .lte('date', dateRange.end.toISOString().split('T')[0])
          .order('date', { ascending: false });

        if (metricsError) throw metricsError;

        // Aggregate metrics for the selected period
        const aggregatedMetrics = metricsData.reduce((acc, day) => ({
          spend: acc.spend + (day.metrics.spend || 0),
          impressions: acc.impressions + (day.metrics.impressions || 0),
          clicks: acc.clicks + (day.metrics.clicks || 0),
          ctr: day.metrics.ctr || 0,
          reach: acc.reach + (day.metrics.reach || 0),
        }), {
          spend: 0,
          impressions: 0,
          clicks: 0,
          ctr: 0,
          reach: 0,
        });

        // Calculate average CTR
        if (metricsData.length > 0) {
          aggregatedMetrics.ctr = aggregatedMetrics.ctr / metricsData.length;
        }

        setMetrics(aggregatedMetrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedPlatform, selectedAccount, timeRange, customDateRange]);

  const handleRefresh = async () => {
    if (refreshing || !selectedPlatform || !selectedAccount) return;
    
    setRefreshing(true);
    try {
      const data = await fetchMetaAdsData();
      if (data?.metrics) {
        const dateRange = getDateRange();
        const filteredMetrics = data.metrics.filter(day => {
          const date = new Date(day.date);
          return date >= dateRange.start && date <= dateRange.end;
        });

        const aggregatedMetrics = filteredMetrics.reduce((acc, day) => ({
          spend: acc.spend + (day.metrics.spend || 0),
          impressions: acc.impressions + (day.metrics.impressions || 0),
          clicks: acc.clicks + (day.metrics.clicks || 0),
          ctr: day.metrics.ctr || 0,
          reach: acc.reach + (day.metrics.reach || 0),
        }), {
          spend: 0,
          impressions: 0,
          clicks: 0,
          ctr: 0,
          reach: 0,
        });

        setMetrics(aggregatedMetrics);
      }
    } catch (error) {
      console.error('Error refreshing metrics:', error);
      setError('Failed to refresh analytics data');
    } finally {
      setRefreshing(false);
    }
  };

  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'last7days':
        return 'Last 7 Days';
      case 'last30days':
        return 'Last 30 Days';
      case 'thisMonth':
        return 'This Month';
      case 'lastMonth':
        return 'Last Month';
      case 'custom':
        return `${customDateRange.start.toLocaleDateString()} - ${customDateRange.end.toLocaleDateString()}`;
      default:
        return '';
    }
  };

  const MetricCard = ({ title, value, change, icon: Icon, trend = 'up', subtitle = '' }) => (
    <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className={`${trend === 'up' ? 'bg-blue-500/10' : 'bg-red-500/10'} p-3 rounded-lg`}>
          <Icon className={`h-6 w-6 ${trend === 'up' ? 'text-blue-400' : 'text-red-400'}`} />
        </div>
      </div>
      <div className="flex items-center">
        {trend === 'up' ? (
          <TrendingUp className="h-4 w-4 text-green-400 mr-1" />
        ) : (
          <TrendingDown className="h-4 w-4 text-red-400 mr-1" />
        )}
        <span className={`text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
          {change}
        </span>
      </div>
    </div>
  );

  if (connectionsLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (connectionsError || error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Error Loading Analytics</h3>
          <p className="text-gray-400">{connectionsError || error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <select
              value={selectedPlatform || ''}
              onChange={(e) => {
                setSelectedPlatform(e.target.value);
                setSelectedAccount(null);
              }}
              className="appearance-none bg-[#151632]/50 border border-[#1E2048] rounded-lg pl-4 pr-10 py-2 text-white focus:outline-none focus:border-purple-500 hover:border-purple-500/50 transition-colors"
            >
              <option value="">All Platforms</option>
              {connections.map((conn) => {
                const platform = platforms.find((p) => p.id === conn.platformId);
                return platform ? (
                  <option key={conn.id} value={conn.platformId}>
                    {platform.name}
                  </option>
                ) : null;
              })}
            </select>
            <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {selectedPlatform && accounts.length > 0 && (
            <div className="relative">
              <select
                value={selectedAccount || ''}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="appearance-none bg-[#151632]/50 border border-[#1E2048] rounded-lg pl-4 pr-10 py-2 text-white focus:outline-none focus:border-purple-500 hover:border-purple-500/50 transition-colors"
              >
                <option value="">All Accounts</option>
                {accounts.map((account) => (
                  <option key={account.account_id} value={account.account_id}>
                    {account.name} {account.business_name ? `(${account.business_name})` : ''}
                  </option>
                ))}
              </select>
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          )}
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="appearance-none bg-[#151632]/50 border border-[#1E2048] rounded-lg pl-4 pr-10 py-2 text-white focus:outline-none focus:border-purple-500 hover:border-purple-500/50 transition-colors"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
            <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {timeRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateRange.start.toISOString().split('T')[0]}
                onChange={(e) => setCustomDateRange(prev => ({
                  ...prev,
                  start: new Date(e.target.value)
                }))}
                className="bg-[#151632]/50 border border-[#1E2048] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500 hover:border-purple-500/50 transition-colors"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customDateRange.end.toISOString().split('T')[0]}
                onChange={(e) => setCustomDateRange(prev => ({
                  ...prev,
                  end: new Date(e.target.value)
                }))}
                className="bg-[#151632]/50 border border-[#1E2048] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500 hover:border-purple-500/50 transition-colors"
              />
            </div>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || !selectedPlatform || !selectedAccount}
          className="flex items-center px-4 py-2 text-sm font-medium text-blue-400 hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Key Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Spend"
            value={`$${metrics.spend.toFixed(2)}`}
            subtitle={getTimeRangeLabel()}
            change="+12.5% vs last period"
            icon={DollarSign}
            trend="up"
          />
          <MetricCard
            title="Impressions"
            value={metrics.impressions.toLocaleString()}
            subtitle={getTimeRangeLabel()}
            change="+8.3% vs last period"
            icon={Users}
            trend="up"
          />
          <MetricCard
            title="Click-Through Rate"
            value={`${metrics.ctr.toFixed(2)}%`}
            subtitle={getTimeRangeLabel()}
            change="+0.5 vs last period"
            icon={Target}
            trend="up"
          />
          <MetricCard
            title="Total Reach"
            value={metrics.reach.toLocaleString()}
            subtitle={getTimeRangeLabel()}
            change="+15.2% vs last period"
            icon={Users}
            trend="up"
          />
        </div>
      )}

      {/* Performance Chart */}
      {metrics && (
        <div className="bg-[#151632]/50 backdrop-blur-sm p-6 rounded-xl border border-[#1E2048] hover:border-purple-500/25 transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white font-semibold">Performance Metrics</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue-400 mr-2"></div>
                <span className="text-sm text-gray-400">Impressions</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-400 mr-2"></div>
                <span className="text-sm text-gray-400">Clicks</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[
                { name: 'Week 1', impressions: metrics.impressions * 0.25, clicks: metrics.clicks * 0.25 },
                { name: 'Week 2', impressions: metrics.impressions * 0.5, clicks: metrics.clicks * 0.5 },
                { name: 'Week 3', impressions: metrics.impressions * 0.75, clicks: metrics.clicks * 0.75 },
                { name: 'Week 4', impressions: metrics.impressions, clicks: metrics.clicks },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2048" />
                <XAxis dataKey="name" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#151632',
                    border: '1px solid #1E2048',
                    borderRadius: '0.5rem',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!selectedPlatform && (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-white mb-4">Select a Platform</h2>
          <p className="text-gray-400">Choose a platform from the dropdown above to view analytics data</p>
        </div>
      )}

      {selectedPlatform && !selectedAccount && accounts.length > 0 && (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-white mb-4">Select an Account</h2>
          <p className="text-gray-400">Choose an ad account to view its analytics data</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;